<!-- src/views/pages/superadmin/super-subscription.vue -->
<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import * as bootstrap from "bootstrap";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import svc from "@/services/super-subscription.service";
import { toast } from "@/utils/toast";
import SubscriptionTable from "./subscription-table.vue";
import SubscriptionModal from "@/components/modal/subscription-modal.vue";
import { totalChart, activeChart, inactiveChart, locationChart } from "./data";

const title = "Subscription";
const text = "Superadmin";
const text1 = "Subscription";

const subscriptionStore = useSubscriptionStore();
const dateRangeInput = ref(null);
const editingSubscription = ref(null);
const sortOption = ref("Recently Added");

function toggleHeader() {
  document.getElementById("collapse-header").classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function booking_range(start, end) {
  subscriptionStore.filters.from = start.format("YYYY-MM-DD");
  subscriptionStore.filters.to = end.format("YYYY-MM-DD");
  applySubscriptionFilters();
  return start.format("M/D/YYYY") + " - " + end.format("M/D/YYYY");
}

function applySubscriptionFilters() {
  subscriptionStore.fetchSubscriptions({ page: 1 });
  subscriptionStore.fetchStats();
}

function setSubscriptionSort(option) {
  sortOption.value = option;

  if (option === "Recently Added") {
    subscriptionStore.filters.sort_by = "created_at";
    subscriptionStore.filters.sort_dir = "desc";
  } else if (option === "Subscriber A-Z") {
    subscriptionStore.filters.sort_by = "company_name";
    subscriptionStore.filters.sort_dir = "asc";
  } else if (option === "Subscriber Z-A") {
    subscriptionStore.filters.sort_by = "company_name";
    subscriptionStore.filters.sort_dir = "desc";
  } else if (option === "Plan A-Z") {
    subscriptionStore.filters.sort_by = "plan_name";
    subscriptionStore.filters.sort_dir = "asc";
  } else if (option === "Expiring Soon") {
    subscriptionStore.filters.sort_by = "ends_at";
    subscriptionStore.filters.sort_dir = "asc";
  }

  applySubscriptionFilters();
}

const handleCreated = () => {
  // extra refresh if needed
  subscriptionStore.fetchSubscriptions({ page: 1 });
  subscriptionStore.fetchStats();
  editingSubscription.value = null;
};

function openAddModal() {
  editingSubscription.value = null;
  const el = document.getElementById("add_subscription");
  if (el) bootstrap.Modal.getOrCreateInstance(el).show();
}

function openEditModal(subscription) {
  editingSubscription.value = subscription;
  const el = document.getElementById("add_subscription");
  if (el) bootstrap.Modal.getOrCreateInstance(el).show();
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function exportSubscriptions(format) {
  try {
    const res = await svc.export(
      {
        from: subscriptionStore.filters.from || undefined,
        to: subscriptionStore.filters.to || undefined,
        payment_status: subscriptionStore.filters.payment_status || undefined,
        status: subscriptionStore.filters.status || undefined,
        plan_id: subscriptionStore.filters.plan_id || undefined,
        sort_by: subscriptionStore.filters.sort_by || undefined,
        sort_dir: subscriptionStore.filters.sort_dir || undefined,
      },
      format
    );

    downloadBlob(
      res.data,
      format === "pdf"
        ? "subscriptions-export.pdf"
        : "subscriptions-export.xlsx"
    );
  } catch (error) {
    toast(
      error?.response?.data?.message || "Failed to export subscriptions",
      "error"
    );
  }
}

onMounted(() => {
  const start = moment().subtract(6, "days");
  const end = moment();

  subscriptionStore.filters.from = start.format("YYYY-MM-DD");
  subscriptionStore.filters.to = end.format("YYYY-MM-DD");

  subscriptionStore.fetchSubscriptions();
  subscriptionStore.fetchStats();

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range
    );
  }
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-platform">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportSubscriptions('pdf')"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportSubscriptions('excel')"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Add Subscription button -->
          <div class="me-2 mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAddModal"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Subscription
            </a>
          </div>

          <div class="head-icons">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Stats row -->
      <div class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="border-bottom pb-3 mb-3">
                <div class="row align-items-center">
                  <div class="col-7">
                    <span class="fs-14 fw-normal text-truncate mb-1">
                      Total Transaction
                    </span>
                    <h5>
                      KES
                      {{
                        subscriptionStore.stats.total_transactions.toFixed(2)
                      }}
                    </h5>
                  </div>
                  <div class="col-5">
                    <apexchart
                      type="area"
                      width="50"
                      :options="totalChart.total"
                      :series="totalChart.series"
                    />
                  </div>
                </div>
              </div>
              <p
                class="fs-12 fw-normal d-flex align-items-center text-truncate"
              >
                <span class="text-primary fs-12 d-flex align-items-center me-1">
                  <i class="ti ti-check me-1"></i>Live</span
                >from current filter
              </p>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="border-bottom pb-3 mb-3">
                <div class="row align-items-center">
                  <div class="col-7">
                    <span class="fs-14 fw-normal text-truncate mb-1">
                      Total Subscribers
                    </span>
                    <h5>{{ subscriptionStore.stats.total_subscribers }}</h5>
                  </div>
                  <div class="col-5">
                    <apexchart
                      type="area"
                      width="50"
                      :options="activeChart.active"
                      :series="activeChart.series"
                    />
                  </div>
                </div>
              </div>
              <p
                class="fs-12 fw-normal d-flex align-items-center text-truncate"
              >
                <span class="text-primary fs-12 d-flex align-items-center me-1">
                  <i class="ti ti-check me-1"></i>Live</span
                >from current filter
              </p>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="border-bottom pb-3 mb-3">
                <div class="row align-items-center">
                  <div class="col-7">
                    <span class="fs-14 fw-normal text-truncate mb-1">
                      Active Subscribers
                    </span>
                    <h5>{{ subscriptionStore.stats.active_subscribers }}</h5>
                  </div>
                  <div class="col-5">
                    <apexchart
                      type="area"
                      width="50"
                      :options="inactiveChart.inactive"
                      :series="inactiveChart.series"
                    />
                  </div>
                </div>
              </div>
              <p
                class="fs-12 fw-normal d-flex align-items-center text-truncate"
              >
                <span class="text-primary fs-12 d-flex align-items-center me-1">
                  <i class="ti ti-check me-1"></i>Live</span
                >from current filter
              </p>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="border-bottom pb-3 mb-3">
                <div class="row align-items-center">
                  <div class="col-7">
                    <span class="fs-14 fw-normal text-truncate mb-1">
                      Expired Subscribers
                    </span>
                    <h5>{{ subscriptionStore.stats.expired_subscribers }}</h5>
                  </div>
                  <div class="col-5">
                    <apexchart
                      type="area"
                      width="50"
                      :options="locationChart.location"
                      :series="locationChart.series"
                    />
                  </div>
                </div>
              </div>
              <p
                class="fs-12 fw-normal d-flex align-items-center text-truncate"
              >
                <span class="text-primary fs-12 d-flex align-items-center me-1">
                  <i class="ti ti-check me-1"></i>Live</span
                >from current filter
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- List -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Subscription List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Select Status
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.payment_status = 'paid';
                      applySubscriptionFilters();
                    "
                  >
                    Paid
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.payment_status = 'unpaid';
                      applySubscriptionFilters();
                    "
                  >
                    Unpaid
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.payment_status = 'pending';
                      applySubscriptionFilters();
                    "
                  >
                    Pending
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.payment_status = '';
                      applySubscriptionFilters();
                    "
                  >
                    All Payment States
                  </a>
                </li>
              </ul>
            </div>

            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Access Status
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li v-for="value in ['active','grace_period','locked','suspended','expired']" :key="value">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.status = value;
                      applySubscriptionFilters();
                    "
                  >
                    {{ value.replaceAll('_', ' ') }}
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      subscriptionStore.filters.status = '';
                      applySubscriptionFilters();
                    "
                  >
                    All Access States
                  </a>
                </li>
              </ul>
            </div>

            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortOption }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSubscriptionSort('Recently Added')"
                  >
                    Recently Added
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSubscriptionSort('Subscriber A-Z')"
                  >
                    Subscriber A-Z
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSubscriptionSort('Subscriber Z-A')"
                  >
                    Subscriber Z-A
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSubscriptionSort('Plan A-Z')"
                  >
                    Plan A-Z
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSubscriptionSort('Expiring Soon')"
                  >
                    Expiring Soon
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <SubscriptionTable @edit="openEditModal" />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <!-- Add Subscription Modal -->
  <SubscriptionModal :subscription="editingSubscription" @created="handleCreated" />
</template>


